import React, { useEffect, useRef, useState } from "react";
import { Button, Table, Modal, Tag, Empty, Spin } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useOnboarding } from "../../../../contexts/OnboardingContext";
import { useToast } from "../../../../contexts/ToastContext";
import { useCarContext } from "../../../../contexts/CarContext";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import AddCarModal from "../../../cars/components/modals/AddCarModal";
import VinVerifyStep from "../../../cars/components/addCar/VinVerifyStep";
import deleteFile from "../../../../utilities/deleteFile";

const AddCar = () => {
    const { t } = useTranslation("onboarding");
    const { updateStepProgress, stepStatus } = useOnboarding();
    const { cars, loading, fetchCars, deleteCar } = useCarContext();
    const { totalLocations, fetchLocations, verifyCarCreationPrerequisites } = useCategoriesContext();
    const toast = useToast();

    const [addCarModal, setAddCarModal] = useState(false);
    const [decodedVehiclePrefill, setDecodedVehiclePrefill] = useState(null);
    const [manualVinPrefill, setManualVinPrefill] = useState("");
    const [noLocationsModal, setNoLocationsModal] = useState(false);
    const [checkingAddCarPrerequisites, setCheckingAddCarPrerequisites] = useState(false);

    const prevCarCountRef = useRef(cars.length);

    useEffect(() => {
        verifyCarCreationPrerequisites();
        fetchLocations();
        fetchCars();
    }, [verifyCarCreationPrerequisites, fetchLocations, fetchCars]);

    useEffect(() => {
        if (!stepStatus["add-car"] && cars.length > 0) {
            updateStepProgress("add-car", 3, true);
        }
    }, [cars.length, updateStepProgress, stepStatus]);

    useEffect(() => {
        if (cars.length > prevCarCountRef.current) {
            setDecodedVehiclePrefill(null);
        }
        prevCarCountRef.current = cars.length;
    }, [cars.length]);

    const prerequisitesOk = totalLocations > 0;

    const handleModalClose = () => {
        setAddCarModal(false);
        setDecodedVehiclePrefill(null);
        setManualVinPrefill("");
    };

    const openManualModal = (vinValue = "") => {
        setDecodedVehiclePrefill(null);
        setManualVinPrefill(vinValue || "");
        setAddCarModal(true);
    };

    const handleContinueFromVin = (decoded) => {
        setDecodedVehiclePrefill(decoded);
        setAddCarModal(true);
    };

    const handleAddCarClick = async () => {
        setCheckingAddCarPrerequisites(true);
        try {
            const { hasLocations } = await verifyCarCreationPrerequisites();
            if (!hasLocations) {
                setNoLocationsModal(true);
                return;
            }
            openManualModal();
        } finally {
            setCheckingAddCarPrerequisites(false);
        }
    };

    const handleConfirmDelete = async (car) => {
        toast.api.destroy();

        const result = await deleteCar(car?.id);

        if (car?.images && Object.keys(car.images).length > 0) {
            const imageValues = Object.values(car.images);
            await Promise.all(
                imageValues.map((image) => {
                    if (image) {
                        return deleteFile(image);
                    }
                    return Promise.resolve();
                }),
            );
        }

        if (result.success) {
            toast.success({
                message: t("common.delete"),
                description: t("steps.addCar.carDeleted"),
            });
        } else {
            toast.error({
                message: t("messages.saveError"),
                description: t("steps.addCar.failedToDelete"),
            });
        }
    };

    const handleDeleteCar = async (car) => {
        Modal.confirm({
            title: t("steps.addCar.confirmDelete"),
            content: (
                <div>
                    <p>
                        {car?.carNumber
                            ? t("steps.addCar.deleteCarMessage", { plateNumber: car.carNumber })
                            : t("steps.addCar.deleteCarConfirm")}
                    </p>
                </div>
            ),
            okText: t("common.confirm"),
            cancelText: t("common.cancel"),
            onOk: () => handleConfirmDelete(car),
            onCancel: () => toast.api.destroy(),
            centered: true,
            okButtonProps: {
                danger: true,
            },
        });
    };

    const columns = [
        {
            title: t("steps.addCar.carNumber"),
            dataIndex: "carNumber",
            key: "carNumber",
        },
        {
            title: t("steps.addCar.makeModel"),
            key: "makeModel",
            render: (_, record) =>
                `${record.config?.make || ""} ${record.config?.model || ""}`.trim() || t("common.notAvailable"),
        },
        {
            title: t("steps.addCar.year"),
            key: "year",
            render: (_, record) => record.config?.year || t("common.notAvailable"),
        },
        {
            title: t("common.actions"),
            key: "actions",
            render: (_, record) => (
                <Button
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteCar(record)}
                    size="small"
                    danger
                    disabled={loading}>
                    {t("common.delete")}
                </Button>
            ),
        },
    ];

    const renderVinFlow = () => (
        <VinVerifyStep
            translationNamespace="onboarding"
            translationKeyPrefix="steps.addCar"
            variant="onboarding"
            onContinue={handleContinueFromVin}
            onManualEntry={openManualModal}
        />
    );

    return (
        <div>
            <h1 className="text-[28px] font-bold tracking-tight text-gray-900 sm:text-[32px]">
                {t("steps.addCar.heading")}
            </h1>
            <p className="mt-1 mb-8 text-sm leading-relaxed text-gray-500 sm:text-base">
                {t("steps.addCar.description")}
            </p>

            {!prerequisitesOk ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <p className="text-gray-500 mb-4">{t("steps.addCar.prerequisitesMessageLocationsOnly")}</p>
                    <Tag color="warning">{t("steps.addCar.addLocations")}</Tag>
                </div>
            ) : (
                <>
                    {cars.length === 0 ? (
                        loading ? (
                            <div className="flex justify-center py-16">
                                <Spin size="large" />
                            </div>
                        ) : (
                            renderVinFlow()
                        )
                    ) : (
                        <div className="space-y-8">
                            {renderVinFlow()}
                            <div>
                                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        {t("steps.addCar.yourFleet")}
                                    </h3>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={handleAddCarClick}
                                        size="large"
                                        className="rounded-full border-none bg-[#5D5DF6] hover:bg-[#4e4ee8]"
                                        disabled={loading || checkingAddCarPrerequisites}
                                        loading={checkingAddCarPrerequisites}>
                                        {t("steps.addCar.addVehicle")}
                                    </Button>
                                </div>
                                <Table
                                    className="sc-table-frame"
                                    columns={columns}
                                    dataSource={cars}
                                    rowKey="id"
                                    pagination={false}
                                    loading={loading}
                                    locale={{
                                        emptyText: <Empty description={t("steps.addCar.noVehicles")} />,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            <AddCarModal
                open={addCarModal}
                onClose={handleModalClose}
                refetchCars={fetchCars}
                decodedVehiclePrefill={decodedVehiclePrefill}
                entryFlow="manual"
                manualVinPrefill={manualVinPrefill}
            />

            <Modal
                title={<h2 className="text-2xl font-bold">{t("steps.addCar.noLocationsTitle")}</h2>}
                open={noLocationsModal}
                onCancel={() => setNoLocationsModal(false)}
                footer={[
                    <button
                        key="cancel"
                        type="button"
                        className="px-4 py-2 mr-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => setNoLocationsModal(false)}>
                        {t("common.cancel")}
                    </button>,
                    <button
                        key="back"
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        onClick={() => setNoLocationsModal(false)}>
                        {t("steps.addCar.goToLocations")}
                    </button>,
                ]}
                centered
                width={400}>
                <p>{t("steps.addCar.noLocationsMessage")}</p>
            </Modal>
        </div>
    );
};

export default AddCar;
